import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useLocation as useGeoLocation } from "../lib/location";
import { toast } from "sonner";
import { Loader2, MapPin, Calendar, ArrowRight, Send, Banknote, CreditCard, Wallet as WalletIcon, CheckCircle2, ChevronLeft, Navigation, Star, Clock, Zap } from "lucide-react";

export default function BookService() {
  const { listingId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { loc, request } = useGeoLocation();
  const [listing, setListing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [when, setWhen] = useState("now");
  const [walletBalance, setWalletBalance] = useState(0);
  const [feePct, setFeePct] = useState(0.10);
  const [form, setForm] = useState({
    pickup_address: "", dropoff_address: "", quantity: 1, notes: "", scheduled_at: "",
  });

  useEffect(() => {
    api.get(`/listings/${listingId}`).then((r) => setListing(r.data)).catch(() => nav("/services"));
    api.get("/config").then((r) => setFeePct(r.data.platform_fee_pct || 0.10)).catch(() => {});
    if (user?.role === "customer") {
      api.get("/wallet").then((r) => setWalletBalance(r.data.balance || 0)).catch(() => {});
    }
  }, [listingId, nav, user]);

  // Auto-fill pickup with detected location
  useEffect(() => {
    if (loc && !form.pickup_address) {
      const label = loc.label || `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}`;
      setForm((f) => ({ ...f, pickup_address: label }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc]);

  const useCurrent = async () => {
    const l = loc || await request();
    if (l) setForm((f) => ({ ...f, pickup_address: l.label || `${l.lat.toFixed(5)}, ${l.lng.toFixed(5)}` }));
  };

  const total = listing ? (listing.price * (parseFloat(form.quantity) || 1)) : 0;
  const platformFee = total * feePct;
  const supplierNet = total - platformFee;
  const walletInsufficient = paymentMethod === "wallet" && walletBalance < total;

  const submitRequest = async () => {
    if (!user) { nav("/login"); return; }
    if (user.role !== "customer") { toast.error(t("booking.onlyCustomers")); return; }
    if (!form.pickup_address) { toast.error(t("booking.pickupRequired")); return; }
    setBusy(true);
    try {
      const { data: order } = await api.post("/orders", {
        listing_id: listingId,
        pickup_address: form.pickup_address,
        pickup_lat: loc?.lat, pickup_lng: loc?.lng,
        dropoff_address: form.dropoff_address || null,
        quantity: parseFloat(form.quantity) || 1,
        notes: form.notes || null,
        scheduled_at: when === "later" && form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
        payment_method: paymentMethod,
      });
      if (paymentMethod === "card") {
        const { data: co } = await api.post("/payments/checkout", {
          order_id: order.order_id, origin_url: window.location.origin,
        });
        window.location.href = co.checkout_url;
      } else {
        toast.success(t("booking.requestSent"));
        nav("/customer");
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy(false);
    }
  };

  if (!listing) return <div className="p-16 text-center text-xs text-ink-textDim">…</div>;

  return (
    <div className="max-w-[600px] md:max-w-[900px] mx-auto pb-4">
      <div className="px-4 pt-3 pb-2 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-9 h-9 rounded-full bg-ink-surface border border-ink-border flex items-center justify-center rtl-flip" data-testid="back-btn"><ChevronLeft size={18} /></button>
        <div className="text-xl font-black">{t("booking.title")}</div>
      </div>

      <div className="px-4 space-y-3">
        {/* Listing card */}
        <div className="bg-ink-surface rounded-2xl overflow-hidden border border-ink-border flex">
          {listing.image_url && <img src={listing.image_url} alt="" className="w-24 h-24 object-cover" />}
          <div className="flex-1 p-3 min-w-0">
            <div className="text-[10px] uppercase font-black text-white/70">{t(`categories.${listing.category}`)}</div>
            <div className="font-bold text-sm leading-snug line-clamp-2">{listing.title}</div>
            <div className="flex items-center gap-3 mt-1 text-[11px] text-ink-textDim">
              <span className="flex items-center gap-1"><Star size={11} className="text-brand-warn" fill="currentColor" /> {listing.supplier_rating?.toFixed(1) || "—"}</span>
              <span className="flex items-center gap-1"><Clock size={11} /> {listing.eta_minutes || 30}m</span>
            </div>
          </div>
          <div className="p-3 text-end">
            <div className="text-white font-black">${listing.price}</div>
            <div className="text-[10px] text-ink-textDim uppercase font-bold">/{listing.unit}</div>
          </div>
        </div>

        {/* When: Now vs Schedule */}
        <div className="bg-ink-surface rounded-2xl p-4 border border-ink-border" data-testid="when-section">
          <div className="text-[10px] uppercase font-black text-ink-textDim mb-3">{t("booking.when")}</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setWhen("now")} className={`rounded-xl p-3 border-2 text-start ${when === "now" ? "border-white bg-white/5" : "border-ink-border"}`} data-testid="when-now">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${when === "now" ? "bg-white text-black" : "bg-ink-surface2"}`}>
                  <Zap size={14} fill={when === "now" ? "currentColor" : "none"} />
                </div>
                <div>
                  <div className="text-xs font-black">{t("booking.now")}</div>
                  <div className="text-[10px] text-ink-textDim">{t("booking.nowDesc", { min: listing.eta_minutes || 30 })}</div>
                </div>
              </div>
            </button>
            <button type="button" onClick={() => setWhen("later")} className={`rounded-xl p-3 border-2 text-start ${when === "later" ? "border-white bg-white/5" : "border-ink-border"}`} data-testid="when-later">
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${when === "later" ? "bg-white text-black" : "bg-ink-surface2"}`}>
                  <Calendar size={14} />
                </div>
                <div>
                  <div className="text-xs font-black">{t("booking.later")}</div>
                  <div className="text-[10px] text-ink-textDim">{t("booking.laterDesc")}</div>
                </div>
              </div>
            </button>
          </div>
          {when === "later" && (
            <div className="mt-3">
              <div className="text-[10px] uppercase font-black text-ink-textDim mb-1 flex items-center gap-1"><Calendar size={11} /> {t("booking.pickDateTime")}</div>
              <input type="datetime-local" required value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                className="w-full bg-ink-surface2 rounded-xl px-3 py-2.5 text-sm border border-ink-border focus:border-white outline-none" data-testid="scheduled" />
            </div>
          )}
        </div>

        {/* Route */}
        <div className="bg-ink-surface rounded-2xl p-4 border border-ink-border" data-testid="booking-form">
          <div className="text-[10px] uppercase font-black text-ink-textDim mb-3">{t("booking.route")}</div>
          <div className="relative">
            <div className="absolute start-3 top-4 bottom-4 w-px bg-ink-borderStrong" />
            <div className="flex items-start gap-3 relative">
              <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center flex-shrink-0 z-10 mt-1"><div className="w-2 h-2 rounded-full bg-black" /></div>
              <div className="flex-1">
                <div className="text-[10px] uppercase font-black text-ink-textDim">{t("booking.pickup")}</div>
                <div className="flex gap-2 items-center">
                  <input value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })} placeholder={t("booking.pickupPlaceholder")} required
                    className="flex-1 bg-transparent border-b border-ink-border focus:border-white text-sm py-2 outline-none" data-testid="pickup-address" />
                  <button type="button" onClick={useCurrent} className="text-[10px] font-black uppercase bg-white/10 hover:bg-white/20 rounded-full px-2 py-1 flex items-center gap-1" data-testid="use-current-location">
                    <Navigation size={11} /> {t("booking.useMyLocation")}
                  </button>
                </div>
              </div>
            </div>
            {listing.category !== "tire" && listing.category !== "mechanic" && (
              <div className="flex items-start gap-3 mt-4 relative">
                <div className="w-6 h-6 rounded-md bg-white flex items-center justify-center flex-shrink-0 z-10 mt-1"><div className="w-2 h-2 rounded-sm bg-black" /></div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase font-black text-ink-textDim">{t("booking.dropoff")} {t("booking.optional")}</div>
                  <input value={form.dropoff_address} onChange={(e) => setForm({ ...form, dropoff_address: e.target.value })} placeholder={t("booking.dropoffPlaceholder")}
                    className="w-full bg-transparent border-b border-ink-border focus:border-white text-sm py-2 outline-none" data-testid="dropoff-address" />
                </div>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3 mt-4">
            <label className="block">
              <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">{t("booking.quantity")} × {listing.unit}</div>
              <input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                className="w-full bg-ink-surface2 rounded-xl px-3 py-2.5 text-sm border border-ink-border focus:border-white outline-none" data-testid="quantity" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase font-black text-ink-textDim mb-1 flex items-center gap-1"><Clock size={11} /> {t("booking.eta")}</div>
              <div className="w-full bg-ink-surface2 rounded-xl px-3 py-2.5 text-sm border border-ink-border">{when === "later" && form.scheduled_at ? new Date(form.scheduled_at).toLocaleString(undefined, { hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" }) : `~${listing.eta_minutes || 30} ${t("booking.min")}`}</div>
            </label>
          </div>
          <label className="block mt-3">
            <div className="text-[10px] uppercase font-black text-ink-textDim mb-1">{t("booking.notes")}</div>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full bg-ink-surface2 rounded-xl px-3 py-2.5 text-sm border border-ink-border focus:border-white outline-none"
              data-testid="notes" placeholder={t("booking.notesPlaceholder")} />
          </label>
        </div>

        {/* Payment method */}
        <div className="bg-ink-surface rounded-2xl p-4 border border-ink-border" data-testid="payment-method-section">
          <div className="text-[10px] uppercase font-black text-ink-textDim mb-3">{t("payment.method")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <PayOption icon={Banknote} selected={paymentMethod === "cash"} onClick={() => setPaymentMethod("cash")} title={t("payment.cash")} desc={t("payment.cashDesc")} testid="pay-cash" />
            {/* Card payment option removed — Drivon accepts PayPal + Cash. */}
            <PayOption
              icon={WalletIcon}
              selected={paymentMethod === "wallet"}
              onClick={() => setPaymentMethod("wallet")}
              title={t("payment.wallet", "Wallet")}
              desc={walletBalance < total && total > 0 ? `${t("payment.balance", "Balance")}: $${walletBalance.toFixed(2)} — ${t("payment.insufficient", "insufficient")}` : `${t("payment.balance", "Balance")}: $${walletBalance.toFixed(2)}`}
              testid="pay-wallet"
              disabled={walletBalance < total && total > 0}
            />
          </div>
          {walletInsufficient && (
            <div className="mt-2 text-[11px] text-brand-warn" data-testid="wallet-insufficient-msg">
              {t("payment.walletInsufficient", "Wallet balance is not enough for this order.")} <button type="button" onClick={() => nav("/wallet")} className="underline font-bold">{t("payment.topUp", "Top up")}</button>
            </div>
          )}
        </div>

        {/* Fee breakdown */}
        <div className="bg-ink-surface rounded-2xl p-4 border border-ink-border" data-testid="fee-breakdown">
          <div className="text-[10px] uppercase font-black text-ink-textDim mb-3">{t("booking.summary")}</div>
          <Row k={t("booking.refPrice")} v={`$${listing.price} / ${listing.unit}`} />
          <Row k={t("booking.quantity")} v={`× ${form.quantity || 1}`} />
          <div className="h-px bg-ink-border my-2" />
          <Row k={t("payment.total")} v={`$${total.toFixed(2)}`} strong />
          <Row k={t("payment.platformFee")} v={`$${platformFee.toFixed(2)} (${(feePct * 100).toFixed(0)}%)`} muted />
          <Row k={t("payment.supplierGets")} v={`$${supplierNet.toFixed(2)}`} muted />
          <div className="mt-2 text-[10px] text-ink-textDim">{t("payment.feeNote")}</div>
        </div>
      </div>

      {/* Sticky checkout */}
      <div className="fixed bottom-16 left-0 right-0 z-30 bg-ink-bg border-t border-ink-border p-3" data-testid="checkout-bar">
        <div className="max-w-[600px] md:max-w-[900px] mx-auto flex items-center gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase font-black text-ink-textDim">{t("booking.estimate")}</div>
            <div className="text-white font-black text-2xl">${total.toFixed(2)}</div>
          </div>
          <button onClick={submitRequest} disabled={busy || walletInsufficient} className="pill-btn bg-white disabled:opacity-50 text-black rounded-full px-5 py-3 text-sm font-black inline-flex items-center gap-2" data-testid="checkout-btn">
            {busy ? <Loader2 className="animate-spin" size={16} /> : (paymentMethod === "card" ? <CreditCard size={16} /> : (paymentMethod === "wallet" ? <WalletIcon size={16} /> : <Send size={16} />))}
            {paymentMethod === "card" ? t("payment.payNow") : (paymentMethod === "wallet" ? t("payment.payWithWallet", "Pay with Wallet") : t("payment.payLater"))}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>

      <div className="h-32" />
    </div>
  );
}

function PayOption({ icon: Icon, selected, onClick, title, desc, testid, badge, disabled }) {
  return (
    <button onClick={onClick} type="button" disabled={disabled} className={`text-start rounded-xl p-3 border-2 transition-all ${selected ? "border-white bg-white/5" : "border-ink-border bg-transparent hover:border-ink-borderStrong"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`} data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${selected ? "bg-white text-black" : "bg-ink-surface2 text-white"}`}>
          <Icon size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black">{title}</div>
          <div className="text-[10px] text-ink-textDim truncate">{desc}</div>
          {badge}
        </div>
        {selected && <CheckCircle2 size={16} className="text-white" />}
      </div>
    </button>
  );
}

function ApplePayLogo() {
  return <div className="bg-black border border-white/20 rounded px-1.5 py-0.5 flex items-center gap-0.5 text-[9px] font-black text-white" title="Apple Pay">
    <svg width="8" height="10" viewBox="0 0 12 15" fill="currentColor" className="text-white"><path d="M9.83 2.5c.44-.53.74-1.26.66-2C9.85.53 9.07.92 8.6 1.44c-.42.46-.77 1.21-.67 1.94.75.05 1.5-.35 1.9-.88zm.66 2.85c-1.05-.06-1.94.6-2.44.6-.5 0-1.26-.57-2.08-.55-1.07.02-2.06.62-2.61 1.58-1.11 1.93-.28 4.79.81 6.36.53.77 1.15 1.63 1.97 1.6.79-.03 1.09-.51 2.04-.51.95 0 1.22.51 2.05.5.85-.02 1.39-.78 1.91-1.55.6-.89.85-1.75.87-1.8-.02-.01-1.67-.64-1.69-2.55-.02-1.6 1.31-2.36 1.37-2.4-.75-1.1-1.92-1.22-2.34-1.28z"/></svg>
    Pay
  </div>;
}
function GooglePayLogo() {
  return <div className="bg-white text-black rounded px-1.5 py-0.5 text-[9px] font-black" title="Google Pay">G Pay</div>;
}

function Row({ k, v, strong, muted }) {
  return (
    <div className={`flex justify-between text-sm py-1 ${muted ? "text-ink-textDim" : ""}`}>
      <span>{k}</span>
      <span className={strong ? "font-black" : ""}>{v}</span>
    </div>
  );
}
